import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';

export interface BufferedTextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
    value: string;
    onValueChange: (val: string) => void;
    debounceMs?: number;
}

/**
 * Textarea con estado local desacoplado y buffer con debounce de 400ms:
 * - No llama a onValueChange en cada pulsación de tecla para proteger el rendimiento
 *   y la composición IME de teclados móviles.
 * - Emite el valor con una pausa de 400ms (debounce) o de forma inmediata y sincrónica al salir del campo (blur).
 * - Garantiza que los botones de confirmación lean siempre el último texto escrito.
 */
export const BufferedTextArea: React.FC<BufferedTextAreaProps> = ({
    value,
    onValueChange,
    className,
    placeholder,
    disabled,
    style,
    debounceMs = 400,
    ...rest
}) => {
    const [localVal, setLocalVal] = useState(value || '');
    const localValRef = useRef(value || '');
    const lastEmittedRef = useRef(value || '');
    const timerRef = useRef<any>(null);
    const onValueChangeRef = useRef(onValueChange);
    onValueChangeRef.current = onValueChange;

    const flush = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (localValRef.current !== lastEmittedRef.current) {
            const valToEmit = localValRef.current;
            lastEmittedRef.current = valToEmit;
            try {
                flushSync(() => {
                    onValueChangeRef.current(valToEmit);
                });
            } catch {
                onValueChangeRef.current(valToEmit);
            }
        }
    }, []);

    // Sincronizar desde fuera si el valor del padre cambia y no proviene de nuestra propia emisión
    useEffect(() => {
        const next = value || '';
        if (next !== lastEmittedRef.current && next !== localValRef.current) {
            setLocalVal(next);
            localValRef.current = next;
            lastEmittedRef.current = next;
        }
    }, [value]);

    // Limpieza al desmontar: flush inmediato para no perder texto si el modal se desmonta
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            if (localValRef.current !== lastEmittedRef.current) {
                onValueChangeRef.current(localValRef.current);
            }
        };
    }, []);

    return (
        <textarea
            {...rest}
            dir="ltr"
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck="true"
            className={className}
            placeholder={placeholder}
            disabled={disabled}
            style={style}
            value={localVal}
            onChange={(e) => {
                const newVal = e.target.value;
                setLocalVal(newVal);
                localValRef.current = newVal;
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
                timerRef.current = setTimeout(() => {
                    flush();
                }, debounceMs);
            }}
            onBlur={(e) => {
                flush();
                if (rest.onBlur) {
                    rest.onBlur(e);
                }
            }}
        />
    );
};
